import { getUserSession } from '@/service/UserService.js'
import { useKinectinStore } from '@/store/kinectin.js'
import { storeToRefs } from 'pinia'
import { linkIdToId } from '@/service/uiUtils.js'
import { DateTime } from 'luxon'
import { size } from 'lodash-es'
import { toOrgUserTags } from '../../../api/common/sharedUtils.js'

const unauthenticatedRoutes = [
    'signin',
    'signup',
    'signupwith-orgUrlSegment',
    'signinwith-orgUrlSegment',
    'privacy',
    'terms',
    'about',
    'auth_callback',
    'berightback',
]
const webAppHideDrawerRoutes = ['onboard', ...unauthenticatedRoutes]
const nativeAppHideDrawerRoutes = ['onboard', ...unauthenticatedRoutes]

function userHasRequiredScreenerTags({ orgId, userProfile, userTags }) {
    if (!Array.isArray(userTags) || !userTags.length) return true
    const tags = userProfile.value.tags || []
    const orgUserTags = toOrgUserTags({ orgId, userTags: tags })
    return userTags.every(userTag => orgUserTags.includes(userTag) || tags.includes(userTag))
}

function requiredScreenerIsActiveForUser({ orgId, userProfile, requiredScreener, now }) {
    const screenerId = requiredScreener.screenerId
    const startDate = DateTime.fromFormat(requiredScreener.startDate, 'yyyy-MM-dd')
    const endDate = DateTime.fromFormat(requiredScreener.endDate, 'yyyy-MM-dd')
    return screenerId &&
        startDate.isValid &&
        endDate.isValid &&
        userHasRequiredScreenerTags({ orgId, userProfile, userTags: requiredScreener.userTags }) &&
        startDate <= now &&
        now < endDate
}

function timeFragmentIsInRange({ timeFragment, startDate, endDate, now }) {
    let parsed = DateTime.fromFormat(timeFragment, 'yyyy-MM-dd')
    if (!parsed.isValid) {
        parsed = DateTime.fromFormat(timeFragment, 'yyyy-MM')
        if (parsed.year === now.year && parsed.month === now.month) {
            parsed = parsed.set({ day: now.day })
        }
    }
    return parsed.isValid && startDate <= parsed && parsed < endDate
}

async function hasTakenRequiredScreener({ store, orgId, screenerId, startDate, endDate, now }) {
    const screener = await store.getOrgScreener({ orgId, screenerId })
    const questionsSize = size(screener.formConfig.questions)
    await store.loadRecentScreenerAnswers({ orgId, screenerId })
    const results = await store.getRecentScreenerAnswers({ orgId, screenerId })
    for (const result of (results || []).filter(e => e.timeFragment)) {
        if (timeFragmentIsInRange({ timeFragment: result.timeFragment, startDate, endDate, now }) &&
            questionsSize === size(result.answers))
        {
            return true
        }
    }
    return false
}

function isRealRouteChange({ to, from }) {
    return to.name !== from.name || to.path !== from.path
}

function setupRouterMiddleware({ router }) {
    router.beforeEach(async (to, from, next) => {
        if (['berightback', 'privacy', 'terms', 'about', 'auth_callback'].includes(to.name)) return next()

        const store = useKinectinStore()
        const { maintenanceMode, userIsAuthenticated, userProfile } = storeToRefs(store)

        if (maintenanceMode.value) return next({ path: '/berightback', query: { redirect: to.fullPath } })

        if (to.name === 'signinwith-orgUrlSegment') { // special handling here
            const { token } = await getUserSession()
            if (token) return next(to.query.redirect)
        }

        const { token } = await getUserSession()
        if (token) userIsAuthenticated.value = true

        let title = to.path === '/' ? 'Kinectin' : `Kinectin - ${to.path}`
        const baselineOnboarding = to.name === 'assessments-baseline' && to.query.onboarding
        if (unauthenticatedRoutes.includes(to.name) || baselineOnboarding) {
            document.title = title
            return next()
        }
        if (!token) {
            await store.clearCoachGreet() // in case a different user logs in
            if (['signupwith-orgUrlSegment', 'signinwith-orgUrlSegment'].includes(to.name)) {
                return next()
            }
            return next({ path: '/signup', query: { redirect: to.fullPath } })
        }
        const orgCacheExpired = isRealRouteChange({ to, from }) && store.expireOrgCacheIfNeeded()
        await store.loadUserProfile({ noCache: orgCacheExpired })

        if (
            !userProfile.value.onboarded &&
            !['assessments-baseline', 'auth_callback', 'billing', 'onboard'].includes(to.name)
        ) {
            return next({ path: '/auth_callback' })
        }

        const fnsByRouteName = {
            'orgs': store.userCanManageOrgIds,
            'org-orgId': store.userCanManageOrgIds,
        }
        const fn = fnsByRouteName[to.name]
        if (fn && !fn()) return next({ path: '/chat/new' })

        // if the user is in range of a required screener, redirect to the screener if they haven't taken it yet
        const orgId = userProfile.value.orgId
        if (orgId && userProfile.value.onboarded) {
            const org = await store.getOrgPublicProfile({ orgId })
            const now = DateTime.now()
            const requiredScreeners = org.requiredScreenersConfig?.requiredScreeners || []
            if (
                to.name === 'screeners-screener' &&
                requiredScreeners.some((requiredScreener) =>
                    to.query.screenerId === requiredScreener.screenerId &&
                    requiredScreenerIsActiveForUser({ orgId, userProfile, requiredScreener, now })
                )
            ) {
                return next()
            }
            for (const requiredScreener of requiredScreeners) {
                const screenerId = requiredScreener.screenerId
                const startDate = DateTime.fromFormat(requiredScreener.startDate, 'yyyy-MM-dd')
                const endDate = DateTime.fromFormat(requiredScreener.endDate, 'yyyy-MM-dd')
                if (!screenerId || !startDate.isValid || !endDate.isValid) continue
                if (to.name === 'screeners-screener' && to.query.screenerId === screenerId) continue
                if (!userHasRequiredScreenerTags({ orgId, userProfile, userTags: requiredScreener.userTags })) continue
                if (startDate <= now && now < endDate) {
                    const taken = await hasTakenRequiredScreener({ store, orgId, screenerId, startDate, endDate, now })
                    if (!taken) return next({ path: '/screeners/screener', query: { orgId, screenerId, required: 1 } })
                }
            }
        }

        if (to.name === 'chat-threadId' && to.path !== '/chat/new') {
            const linkId = to.params.threadId
            const threadId = linkIdToId({ linkId })
            await store.selectThread({ threadId })
            const { selectedThread } = storeToRefs(store)
            title = `Kinectin - ${selectedThread.value.name}`
        }
        document.title = title
        return next()
    })
}

export {
    setupRouterMiddleware,
    unauthenticatedRoutes,
    webAppHideDrawerRoutes,
    nativeAppHideDrawerRoutes,
}
